import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import "../css/ItemPage.css";
import { collectionsData } from "../data/data";
import Footer from "./components/Footer";
import { addToBasket, getBasket } from "./code/basket";

function ItemPage({ stock }) {
  const { name, id } = useParams(); // Get artwork ID from URL

  // Get the right collection
  const collection = collectionsData.find(
    (collection) => collection.name === name
  );

  // Get the right item
  const item = collection.items[id];

  const [selectedSize] = useState(Object.keys(item.prices)[0]);

  // Carousel state
  const [currentSlide, setCurrentSlide] = useState(0);
  const totalSlides = item.images.length;

  // Swipe handling state
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);

  // Confirmation message state
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Quantity state
  const [quantity, setQuantity] = useState(1);

  // Stock state (fetched from Firestore)
  const [itemStock, setItemStock] = useState(null);

  
  useEffect(() => {
    const updateStockFromBasket = () => {
      const total = stock[item.id] ?? 0;
      const basket = getBasket();
      const inBasket = basket
        .filter(b => b.id === item.id)
        .reduce((sum, item) => sum + item.quantity, 0);

      setItemStock(Math.max(0, total - inBasket));
    };

    // Run once on mount
    updateStockFromBasket();

    // Listen for basket changes
    window.addEventListener("basketUpdated", updateStockFromBasket);

    return () => {
      window.removeEventListener("basketUpdated", updateStockFromBasket);
    };
  }, [item.id, stock]);

  // Carousel functions
  const showSlide = (index) => {
    if (index >= totalSlides) setCurrentSlide(0);
    else if (index < 0) setCurrentSlide(totalSlides - 1);
    else setCurrentSlide(index);
  };

  const handleNext = () => showSlide(currentSlide + 1);
  const handlePrev = () => showSlide(currentSlide - 1);

  // Swipe detection
  const handleSwipe = () => {
    if (touchStartX === null || touchEndX === null) return;
    const swipeDistance = touchStartX - touchEndX;
    const minSwipeDistance = 50; // sensitivity threshold

    if (swipeDistance > minSwipeDistance) handleNext(); // Swiped left
    if (swipeDistance < -minSwipeDistance) handlePrev(); // Swiped right

    setTouchStartX(null);
    setTouchEndX(null);
  };

  // Quantity handlers
  const handleIncrease = () => {
    if (itemStock !== null && quantity < itemStock) {
      setQuantity((q) => q + 1);
    }
  };

  const handleDecrease = () => setQuantity((q) => Math.max(0, q - 1));

  const handleAddToBasket = useCallback(() => {
    if (quantity > 0) {
      addToBasket(
        item,
        item.getPriceId(selectedSize),
        item.getPrice(selectedSize),
        quantity
      );
      setShowConfirmation(true);
      setTimeout(() => setShowConfirmation(false), 2000);
    }
  }, [item, selectedSize, quantity]);

  return (
    <motion.div
      className="container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      <div className="item-page-container">
        <div className="container-left">
          <div
            className="carousel artwork-image"
            onTouchStart={(e) => setTouchStartX(e.touches[0].clientX)}
            onTouchMove={(e) => setTouchEndX(e.touches[0].clientX)}
            onTouchEnd={handleSwipe}
          >
            <div
              className="carousel-images"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {item.images.map((image, index) => (
                <img
                  src={image}
                  className="carousel-item"
                  key={`${collection.name}.${item.title}.${index}`}
                  alt=""
                  loading="lazy"
                />
              ))}
            </div>

            {item.images.length > 1 && (
              <>
                <button onClick={handlePrev} className="carousel-btn" id="prev">
                  &#10094;
                </button>
                <button onClick={handleNext} className="carousel-btn" id="next">
                  &#10095;
                </button>
              </>
            )}
          </div>

          {item.images.length > 1 && (
            <div className="carousel-thumbnails">
              {item.images.map((image, index) => (
                <img
                  src={image}
                  className={`thumbnail ${currentSlide === index ? "active" : ""}`}
                  onClick={() => showSlide(index)}
                  key={`${collection.name}.${item.title}.thumbnail.${index}`}
                  alt=""
                />
              ))}
            </div>
          )}
        </div>

        <div className="container-right frosted">
          <div className="artwork-title">{item.title}</div>

          <div className="artwork-price">
            <p className="number-font">
              £{item.prices[selectedSize].price}
            </p>
          </div>

          {itemStock === 0 && (
            <p style={{ color: "red", fontWeight: "bold" }}>Out of stock</p>
          )}

          <div className="artwork-text">
            <div className="artwork-description">{item.description.replace(/\n/g, `\n\n`)}</div>
            <div className="artwork-size">SIZE: {selectedSize}</div>
            <div className="artwork-details">{item.details}</div>
          </div>

          <div className="quantity-selector">
            <div onClick={handleDecrease} disabled={quantity === 0} className={`${quantity === 0 ? "button-disabled" : ""}`}>
              –
            </div>
            <span>{quantity}</span>
            <div
              onClick={handleIncrease}
              disabled={itemStock !== null && quantity >= itemStock}
              className={itemStock !== null && quantity >= itemStock ? "button-disabled" : ""}
            >
              +
            </div>
          </div>

          <div className="add-to-basket-wrapper">
            <AddToBasketButton
              onClick={handleAddToBasket}
              disabled={quantity === 0 || itemStock === 0}
            />
            <div
              className={`confirmation-message ${
                showConfirmation ? "confirmation-message-visible" : ""
              }`}
            >
              Added to basket!
            </div>
          </div>
        </div>
      </div>

      <ShopCollectionButton collectionName={collection.name} />
      <Footer />
    </motion.div>
  );
}

function AddToBasketButton({ onClick, disabled }) {
  return (
    <button className="add-to-basket-btn" onClick={onClick} disabled={disabled}>
      Add to basket
    </button>
  );
}

function ShopCollectionButton({ collectionName }) {
  return (
    <div>
      <Link to={`/collections/${collectionName}`} className="nav-button">
        <div>
          <div className="shop-collections-button">
            <div>SHOP COLLECTION</div>
          </div>
        </div>
      </Link>
    </div>
  );
}

export default ItemPage;